import { ApiTagTypes, baseApi, IPaginatedResponse, IPaginatedResponseLegacy } from './baseApi';
import { IAppSecret, IAppSecretCreatePayload, IAppSecretUpdatePayload, IAppsQueryArgs } from './appsApi.types';
import { TimePeriod } from '@components/TimeSeriesBarPlot';
import { ISlimApp } from '@biolibtech/biolib-js';

const appsApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        getAppSecrets: build.query<IPaginatedResponseLegacy<IAppSecret>, { appUuid: string }>({
            query: (arg) => ({
                url: '/resources/secrets/',
                params: { app: arg.appUuid, page_size: 100 },
            }),
            providesTags: (response) => {
                return response
                    ? [
                          ...response.results.map(({ uuid }) => ({ type: ApiTagTypes.appSecret, id: uuid }) as const),
                          { type: ApiTagTypes.appSecret, id: 'LIST' },
                      ]
                    : // an error occurred, but we still want to re-fetch this query when
                      // `{ type: 'Posts', id: 'LIST' }` is invalidated
                      [{ type: ApiTagTypes.appSecret, id: 'LIST' }];
            },
        }),
        createAppSecret: build.mutation<IAppSecret, IAppSecretCreatePayload>({
            query: (body) => ({
                body,
                method: 'POST',
                url: `/resources/secrets/`,
            }),
            invalidatesTags: [{ type: ApiTagTypes.appSecret, id: 'LIST' }],
        }),
        updateAppSecret: build.mutation<IAppSecret, IAppSecretUpdatePayload>({
            query: (data) => {
                const { uuid, ...body } = data;
                return {
                    body,
                    method: 'PATCH',
                    url: `/resources/secrets/${uuid}/`,
                };
            },
        }),
        getAggregatedAppActiveUsers: build.query<
            { date: string; count: number }[],
            {
                appUuid: string;
                timePeriod: TimePeriod;
            }
        >({
            query: (args) => ({
                url: `/apps/${args.appUuid}/metrics/aggregated-active-users/?time_period=${args.timePeriod}`,
            }),
        }),
        getApps: build.query<IPaginatedResponse<ISlimApp>, IAppsQueryArgs>({
            query: (args) => ({
                url: '/apps/',
                params: {
                    page: args.page ?? 1,
                    page_size: args.page_size ?? 10,
                    resource_type: args.resource_type,
                    account_handle: args.account_handle,
                    search: args.search || undefined,
                },
            }),
        }),
    }),
});

export const {
    useCreateAppSecretMutation,
    useGetAggregatedAppActiveUsersQuery,
    useGetAppSecretsQuery,
    useGetAppsQuery,
    useUpdateAppSecretMutation,
} = appsApi;
